import { useState, useRef, useEffect } from "react";
import {
  Button,
  Select,
  Text,
  Loader,
  Paper,
  Table,
  Alert,
} from "@mantine/core";
import { IconUpload, IconInfoCircle } from "@tabler/icons-react";
import { toast } from "sonner";
import {
  usePreviewExcelUploadMutation,
  useProcessExcelUploadMutation,
} from "../api/queries";
import { TrialScope } from "./auth/TrialScope";
import type { ExcelColumnMapping, ExcelPreviewResponse } from "../utils/types";

type Step = "upload" | "map" | "processing";

interface FieldConfig {
  key: keyof ExcelColumnMapping;
  label: string;
  required: boolean;
  description?: string;
}

const REQUIRED_FIELDS: FieldConfig[] = [
  {
    key: "accounting_software_id",
    label: "Account ID",
    required: true,
    description: "Unique identifier for the customer account",
  },
  {
    key: "email",
    label: "Email",
    required: true,
    description: "Customer email address",
  },
  {
    key: "contact_name",
    label: "Contact Name",
    required: true,
    description: "Name of the contact person",
  },
  {
    key: "invoice_id",
    label: "Invoice ID",
    required: true,
    description: "Unique identifier for the invoice",
  },
  {
    key: "amount",
    label: "Amount",
    required: true,
    description: "Invoice amount",
  },
  {
    key: "due_date",
    label: "Due Date",
    required: true,
    description: "Invoice due date",
  },
  {
    key: "date_created",
    label: "Date Created",
    required: true,
    description: "Invoice creation date",
  },
  {
    key: "org_name",
    label: "Organization Name",
    required: true,
    description: "Customer organization name",
  },
];

const OPTIONAL_FIELDS: FieldConfig[] = [
  {
    key: "currency",
    label: "Currency",
    required: false,
    description: "Currency code (e.g., USD, EUR, NIS)",
  },
];

// Auto-detection patterns for common column name variations
const AUTO_DETECT_PATTERNS: Record<string, string[]> = {
  email: ["email", "mail", "e-mail", "email_address", "e_mail", "emailaddress"],
  contact_name: [
    "name",
    "contact_name",
    "contact",
    "first_name",
    "fname",
    "contactname",
    "contact name",
  ],
  amount: ["amount", "total", "sum", "value", "price", "cost"],
  due_date: ["due_date", "due date", "due", "payment_date", "duedate"],
  date_created: [
    "date_created",
    "date created",
    "created_date",
    "invoice_date",
    "date",
    "createddate",
  ],
  accounting_software_id: [
    "account",
    "account_id",
    "accountid",
    "customer_id",
    "customerid",
    "account number",
  ],
  invoice_id: [
    "invoice_id",
    "invoice id",
    "invoice",
    "invoice_number",
    "invoicenumber",
    "inv_id",
  ],
  org_name: [
    "org_name",
    "organization",
    "company",
    "organization_name",
    "company_name",
    "orgname",
  ],
  currency: ["currency", "curr", "cur", "money_type"],
};

function autoDetectMapping(
  headers: string[],
  fieldKey: string,
): string | undefined {
  const patterns = AUTO_DETECT_PATTERNS[fieldKey] || [];
  const lowerHeaders = headers.map((h) => h.toLowerCase().trim());

  for (const pattern of patterns) {
    const idx = lowerHeaders.indexOf(pattern.toLowerCase());
    if (idx !== -1) {
      return headers[idx];
    }
  }

  // Try partial matching
  for (const pattern of patterns) {
    const found = lowerHeaders.find((h) => h.includes(pattern.toLowerCase()));
    if (found) {
      const idx = lowerHeaders.indexOf(found);
      return headers[idx];
    }
  }

  return undefined;
}

interface ExcelUploadWithMappingProps {
  onSuccess?: () => void;
  onCancel?: () => void;
}

export function ExcelUploadWithMapping({
  onSuccess,
  onCancel,
}: ExcelUploadWithMappingProps) {
  const [step, setStep] = useState<Step>("upload");
  const [file, setFile] = useState<File | null>(null);
  const [previewData, setPreviewData] = useState<ExcelPreviewResponse | null>(
    null,
  );
  const [mappings, setMappings] = useState<ExcelColumnMapping>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  const previewMutation = usePreviewExcelUploadMutation();
  const processMutation = useProcessExcelUploadMutation();

  useEffect(() => {
    if (previewData && previewData.headers) {
      // Auto-detect mappings
      const autoMappings: ExcelColumnMapping = {};
      [...REQUIRED_FIELDS, ...OPTIONAL_FIELDS].forEach((field) => {
        const detected = autoDetectMapping(previewData.headers, field.key);
        if (detected) {
          autoMappings[field.key] = detected;
        }
      });
      setMappings(autoMappings);
    }
  }, [previewData]);

  function getExcelUploadErrorMessage(
    error: unknown,
    fallback: string,
  ): string {
    const err = error as {
      response?: { data?: { detail?: unknown } };
      message?: string;
    };
    const detail = err?.response?.data?.detail;
    if (typeof detail === "string") return detail;
    if (typeof detail === "object" && detail !== null && "message" in detail)
      return String((detail as { message?: string }).message);
    if (Array.isArray(detail) && detail.length > 0) {
      const first = detail[0];
      const msg =
        typeof first === "object" && first !== null && "msg" in first
          ? (first as { msg?: string }).msg
          : String(first);
      return msg || fallback;
    }
    return err?.message || fallback;
  }

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    setFile(selectedFile);

    try {
      const data = await previewMutation.mutateAsync(selectedFile);
      setPreviewData(data);
      setStep("map");
    } catch (error) {
      const errorMessage = getExcelUploadErrorMessage(
        error,
        "Please check the file format (e.g. no empty columns, supported date formats).",
      );
      toast.error("Failed to preview Excel file", {
        description: errorMessage,
      });
      setFile(null);
      setPreviewData(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleMappingChange = (
    fieldKey: keyof ExcelColumnMapping,
    value: string | null,
  ) => {
    setMappings((prev) => ({
      ...prev,
      [fieldKey]: value || undefined,
    }));
  };

  const validateMappings = (): boolean => {
    const missing = REQUIRED_FIELDS.filter((field) => !mappings[field.key]);
    if (missing.length > 0) {
      toast.error("Missing required mappings", {
        description: `Please map: ${missing.map((f) => f.label).join(", ")}`,
      });
      return false;
    }
    return true;
  };

  const handleProcess = async () => {
    if (!file || !validateMappings()) return;

    setStep("processing");

    try {
      await processMutation.mutateAsync({
        file,
        columnMappings: mappings,
      });
      toast.success("Excel file processed successfully");
      onSuccess?.();
      // Reset state
      setFile(null);
      setPreviewData(null);
      setMappings({});
      setStep("upload");
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (error) {
      const errorMessage = getExcelUploadErrorMessage(
        error,
        "Please check your mappings and file (e.g. dates like 1.1.2026 are supported).",
      );
      toast.error("Failed to process Excel file", {
        description: errorMessage,
      });
      setStep("map");
    }
  };

  const handleCancel = () => {
    setFile(null);
    setPreviewData(null);
    setMappings({});
    setStep("upload");
    if (fileInputRef.current) fileInputRef.current.value = "";
    onCancel?.();
  };

  if (step === "upload") {
    return (
      <div className="flex flex-col items-center space-y-4 w-full">
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          accept=".xlsx,.xls"
          style={{ display: "none" }}
        />
        <Paper
          withBorder
          p="xl"
          className="w-full max-w-2xl cursor-pointer hover:bg-gray-50 transition-colors"
          onClick={() => fileInputRef.current?.click()}
        >
          <div className="flex flex-col items-center space-y-4">
            <IconUpload size={48} className="text-gray-400" />
            <div className="text-center">
              <Text size="lg" fw={500}>
                Upload Excel File
              </Text>
              <Text size="sm" c="dimmed" mt={4}>
                Click to select or drag and drop your Excel file (.xlsx, .xls)
              </Text>
            </div>
            {previewMutation.isPending && <Loader size="sm" />}
          </div>
        </Paper>
      </div>
    );
  }

  if (step === "processing") {
    return (
      <div className="flex flex-col items-center space-y-4 w-full">
        <Loader size="lg" />
        <Text size="lg" fw={500}>
          Processing Excel file...
        </Text>
        <Text size="sm" c="dimmed">
          This may take a few moments
        </Text>
      </div>
    );
  }

  if (step === "map" && previewData && file) {
    const allFields = [...REQUIRED_FIELDS, ...OPTIONAL_FIELDS];
    const unmappedHeaders = previewData.headers.filter(
      (header) => !Object.values(mappings).includes(header),
    );

    // Count unique invoice IDs in the preview data
    const invoiceIdMapping = mappings.invoice_id;
    const invoiceIdIndex = invoiceIdMapping
      ? previewData.headers.indexOf(invoiceIdMapping)
      : -1;
    const newInvoiceCount =
      invoiceIdIndex >= 0
        ? new Set(
            previewData.sample_rows
              .map((row) => row[invoiceIdIndex])
              .filter((v) => v),
          ).size
        : 0;

    return (
      <div className="flex flex-col space-y-6 w-full max-w-4xl">
        <div>
          <Text size="xl" fw={600} mb={4}>
            Map Excel Columns
          </Text>
          <Text size="sm" c="dimmed">
            Map your Excel columns to the required system fields. Sample data is
            shown below each column header to help you identify the correct
            mapping.
          </Text>
        </div>

        <TrialScope>
          <Alert
            icon={<IconInfoCircle size={16} />}
            title="Trial Account Limit"
            color="blue"
          >
            <Text size="sm">
              Trial accounts are limited to 100 invoices. This upload appears to
              contain approximately {newInvoiceCount} new invoices (based on
              sample data). If you exceed the limit, the upload will be
              rejected.
            </Text>
          </Alert>
        </TrialScope>

        <Paper withBorder p="md" className="overflow-x-auto">
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>System Field</Table.Th>
                <Table.Th>Excel Column</Table.Th>
                <Table.Th>Sample Data</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {allFields.map((field) => {
                const selectedHeader = mappings[field.key];
                const headerIndex = selectedHeader
                  ? previewData.headers.indexOf(selectedHeader)
                  : -1;
                const sampleValues =
                  headerIndex >= 0
                    ? previewData.sample_rows
                        .map((row) => row[headerIndex] || "")
                        .filter((v) => v)
                        .slice(0, 3)
                    : [];

                return (
                  <Table.Tr key={field.key}>
                    <Table.Td>
                      <div>
                        <Text fw={500} size="sm">
                          {field.label}
                          {field.required && (
                            <span className="text-red-500 ml-1">*</span>
                          )}
                        </Text>
                        {field.description && (
                          <Text size="xs" c="dimmed" mt={2}>
                            {field.description}
                          </Text>
                        )}
                      </div>
                    </Table.Td>
                    <Table.Td>
                      <Select
                        placeholder="Select column..."
                        data={[
                          { value: "", label: "Skip (optional only)" },
                          ...previewData.headers.map((header) => ({
                            value: header,
                            label: header,
                          })),
                        ]}
                        value={selectedHeader || ""}
                        onChange={(value) =>
                          handleMappingChange(field.key, value)
                        }
                        error={
                          field.required && !selectedHeader
                            ? "Required"
                            : undefined
                        }
                        size="sm"
                        searchable
                      />
                    </Table.Td>
                    <Table.Td>
                      {sampleValues.length > 0 ? (
                        <div className="flex flex-col space-y-1">
                          {sampleValues.map((val, idx) => (
                            <Text
                              key={idx}
                              size="xs"
                              c="dimmed"
                              className="font-mono bg-gray-50 px-2 py-1 rounded"
                            >
                              {String(val)}
                            </Text>
                          ))}
                        </div>
                      ) : (
                        <Text size="xs" c="dimmed" fs="italic">
                          No sample data
                        </Text>
                      )}
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Paper>

        <div className="flex justify-end space-x-3">
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={processMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleProcess}
            loading={processMutation.isPending}
            disabled={processMutation.isPending}
          >
            Process File
          </Button>
        </div>
      </div>
    );
  }

  // Fallback: avoid blank page (e.g. step "map" but previewData missing after error)
  return (
    <div className="flex flex-col items-center space-y-4 w-full">
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        accept=".xlsx,.xls"
        style={{ display: "none" }}
      />
      <Paper
        withBorder
        p="xl"
        className="w-full max-w-2xl cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => fileInputRef.current?.click()}
      >
        <div className="flex flex-col items-center space-y-4">
          <IconUpload size={48} className="text-gray-400" />
          <div className="text-center">
            <Text size="lg" fw={500}>
              Upload Excel File
            </Text>
            <Text size="sm" c="dimmed" mt={4}>
              Click to select or drag and drop your Excel file (.xlsx, .xls)
            </Text>
          </div>
          {previewMutation.isPending && <Loader size="sm" />}
        </div>
      </Paper>
    </div>
  );
}
